import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import Button from "../../Component/Common/Button";
import Select from "../../Component/Common/Select";
import Input from "../../Component/Common/Input";
import Textarea from "../../Component/Common/Textarea";
import { useDispatch, useSelector } from "react-redux";
import {
  addCoupon,
  selectEditCoupon,
  updateCoupon,
} from "../../store/coupon/coupon.action";
import dayjs from "dayjs";

const CouponCreate = ({ changeTab }) => {
  const dispatch = useDispatch();
  const { subscriptionPlans } = useSelector((state) => state.subscription);
  const { currentCoupon } = useSelector((state) => state.coupon);

  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    maxDiscountAmount: "",
    validFrom: dayjs().format("YYYY-MM-DD"),
    validUntil: "",
    usageLimit: "",
    perTenantLimit: "1",
    applicablePlans: [],
  });

  const [errors, setErrors] = useState({});

  const availablePlans = subscriptionPlans.map((plan) => ({
    label: plan.name + " - " + plan.billingCycle,
    value: plan._id,
  }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = "Coupon code is required";
    } else if (formData.code.length < 3) {
      newErrors.code = "Coupon code must be at least 3 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.discountValue || formData.discountValue <= 0) {
      newErrors.discountValue = "Discount value must be greater than 0";
    }

    if (
      formData.discountType === "percentage" &&
      formData.discountValue > 100
    ) {
      newErrors.discountValue = "Percentage cannot exceed 100%";
    }

    if (formData.validFrom && formData.validUntil) {
      if (new Date(formData.validFrom) >= new Date(formData.validUntil)) {
        newErrors.validUntil = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      ...formData,
      validFrom: dayjs(formData.validFrom).startOf("day").format("YYYY-MM-DD"),
      validUntil: formData.validUntil
        ? dayjs(formData.validUntil).endOf("day").format("YYYY-MM-DD")
        : null,
      code: formData.code.toUpperCase(),
      discountValue: parseFloat(formData.discountValue),
      maxDiscountAmount: formData.maxDiscountAmount
        ? parseFloat(formData.maxDiscountAmount)
        : null,
      usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
      perTenantLimit: formData.perTenantLimit
        ? parseInt(formData.perTenantLimit)
        : null,
    };

    console.log("Coupon ", payload);

    if (currentCoupon) {
      dispatch(updateCoupon(currentCoupon._id, payload));
    } else {
      dispatch(addCoupon(payload));
    }
    changeTab();
  };

  useEffect(() => {
    if (currentCoupon) {
      setFormData({
        code: currentCoupon.code,
        description: currentCoupon.description,
        discountType: currentCoupon.discountType,
        discountValue: currentCoupon.discountValue,
        maxDiscountAmount: currentCoupon.maxDiscountAmount,
        validFrom: dayjs(currentCoupon.validFrom).format("YYYY-MM-DD"),
        validUntil: currentCoupon.validUntil
          ? dayjs(currentCoupon.validUntil).format("YYYY-MM-DD")
          : null,
        usageLimit: currentCoupon.usageLimit,
        perTenantLimit: currentCoupon.perTenantLimit,
        applicablePlans: currentCoupon.applicablePlans,
      });
    }

    return () => {
      dispatch(selectEditCoupon(null));
    };
  }, [currentCoupon]);

  return (
    <Box className="organizationRegister">
      <Typography variant="h6" component="h6">
        Add Coupon
      </Typography>
      <hr className="border-bottom" />
      <form>
        <div className="row gy-4">
          <div className="col-12 d-flex justify-content-end align-items-center">
            <Button type={"button"} className={"submit"} onClick={handleSubmit}>
              {currentCoupon ? "Update" : "Create"}
            </Button>
          </div>
          <div className="col-12 col-lg-6">
            <Input
              inputClass="text-uppercase"
              name="code"
              label="Coupon Code *"
              placeholder="CARE20"
              value={formData.code}
              onChange={(e) => handleChange(e)}
              errorMessage={errors.code}
            />
            <Textarea
              className="mt-4"
              name="description"
              label="Description *"
              placeholder="Enter Description"
              maxLength={50}
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange(e)}
              errorMessage={errors.description}
            />

            <Select
              label="Applicable Plans"
              options={availablePlans}
              multiple
              value={formData.applicablePlans}
              onValueChange={(value) =>
                handleChange({ target: { name: "applicablePlans", value } })
              }
              errorMessage={errors.applicablePlans}
            />
          </div>
          <div className="col-12 col-lg-6">
            <div className="row row-gap-4">
              <div className="col-6">
                <Select
                  label="Discount Type *"
                  placeholder="Select Discount Type"
                  options={[
                    { label: "Percentage", value: "percentage" },
                    { label: "Fixed", value: "fixed" },
                  ]}
                  value={formData.discountType}
                  onValueChange={(value) =>
                    handleChange({ target: { name: "discountType", value } })
                  }
                  errorMessage={errors.discountType}
                />
              </div>
              <div className="col-6">
                <Input
                  type="number"
                  name="discountValue"
                  label="Discount Value *"
                  placeholder="Enter Discount Value"
                  value={formData.discountValue}
                  onChange={(e) => handleChange(e)}
                  errorMessage={errors.discountValue}
                />
              </div>
              <div className="col-6">
                <Input
                  type="number"
                  name="maxDiscountAmount"
                  label="Max Discount Amount"
                  placeholder="if Discount Type is Percentage"
                  value={formData.maxDiscountAmount}
                  onChange={(e) => handleChange(e)}
                  errorMessage={errors.maxDiscountAmount}
                />
              </div>
            </div>
            <div className="row row-gap-4 mt-4">
              <div className="col-6">
                <Input
                  label="Valid From"
                  type="date"
                  name="validFrom"
                  value={formData.validFrom}
                  onChange={handleChange}
                  errorMessage={errors.validFrom}
                />
              </div>
              <div className="col-6">
                <Input
                  label="Valid Until"
                  type="date"
                  name="validUntil"
                  value={formData.validUntil}
                  onChange={(e) => handleChange(e)}
                  errorMessage={errors.validUntil}
                />
              </div>
              <div className="col-6">
                <Input
                  type="number"
                  name="usageLimit"
                  label="Usage Limit"
                  placeholder="Unlimited"
                  value={formData.usageLimit}
                  onChange={(e) => handleChange(e)}
                  errorMessage={errors.usageLimit}
                />
              </div>
              <div className="col-6">
                <Input
                  type="number"
                  name="perTenantLimit"
                  label="Per Tenant Limit"
                  placeholder="Enter Per Tenant Limit"
                  value={formData.perTenantLimit}
                  onChange={(e) => handleChange(e)}
                  errorMessage={errors.perTenantLimit}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </Box>
  );
};

export default CouponCreate;
